import { Search, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
  KeyboardEvent,
} from "react";

export interface SearchFilterHandle {
  /** Clear the input and fire onChange("") synchronously. */
  clear: () => void;
  /** Focus the input. */
  focus: () => void;
  /** Force-fire the current value immediately, bypassing the remaining debounce. */
  flush: () => void;
}

export interface SearchFilterProps {
  /** Controlled value. If omitted, the component is uncontrolled. */
  value?: string;
  /** Initial value when uncontrolled. */
  defaultValue?: string;
  /** Fired after `debounceMs` of stillness, or immediately on clear. */
  onChange: (value: string) => void;
  /** Optional callback fired on every keystroke (not debounced). */
  onChangeRaw?: (value: string) => void;
  placeholder?: string;
  /** Debounce window in milliseconds. Default: 400. */
  debounceMs?: number;
  /** Tailwind size on the wrapper div (defaults to `w-64`). */
  className?: string;
  /** Extra classes on the input itself. */
  inputClassName?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** sm = py-1.5 text-xs, md = py-2 text-sm (default), lg = py-2.5 text-base */
  size?: "sm" | "md" | "lg";
  /** Show a Cmd/Ctrl-K hint chip on the right when empty. */
  showShortcutHint?: boolean;
  /** Aria label for the input element (defaults to placeholder). */
  ariaLabel?: string;
  /** id passed through to the input — useful for form labels. */
  id?: string;
  /** name passed through to the input. */
  name?: string;
}

const sizeMap = {
  sm: "py-1.5 text-xs",
  md: "py-2 text-sm",
  lg: "py-2.5 text-base",
} as const;

/**
 * Production-ready debounced search input.
 *
 * Behavior:
 *   - Local state for instant UI feedback while typing.
 *   - `onChange` is called after `debounceMs` of inactivity (default 400ms).
 *   - Clear button (X) fires `onChange("")` IMMEDIATELY and cancels any pending debounce.
 *   - Escape key clears.
 *   - Cmd/Ctrl-K focuses the field (if `showShortcutHint` is true; the binding works regardless).
 *   - Synchronizes with controlled `value` prop without re-firing onChange.
 */
const SearchFilter = forwardRef<SearchFilterHandle, SearchFilterProps>(function SearchFilter(
  {
    value,
    defaultValue,
    onChange,
    onChangeRaw,
    placeholder = "Search…",
    debounceMs = 400,
    className = "w-64",
    inputClassName = "",
    autoFocus,
    disabled,
    size = "md",
    showShortcutHint = false,
    ariaLabel,
    id,
    name,
  },
  ref,
) {
  const isControlled = value !== undefined;
  const [local, setLocal] = useState<string>(isControlled ? value! : defaultValue ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | undefined>(undefined);
  // Track the last value we already pushed via onChange so external `value` syncs don't loop.
  const lastEmittedRef = useRef<string>(local);

  // Sync external controlled value into local state without firing onChange.
  useEffect(() => {
    if (isControlled && value !== local) {
      setLocal(value!);
      lastEmittedRef.current = value!;
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Cleanup any pending timer on unmount.
  useEffect(() => () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
  }, []);

  const fireDebounced = useCallback(
    (next: string) => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        if (lastEmittedRef.current !== next) {
          lastEmittedRef.current = next;
          onChange(next);
        }
      }, debounceMs);
    },
    [debounceMs, onChange],
  );

  const fireNow = useCallback(
    (next: string) => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
      if (lastEmittedRef.current !== next) {
        lastEmittedRef.current = next;
        onChange(next);
      }
    },
    [onChange],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setLocal(next);
    onChangeRaw?.(next);
    fireDebounced(next);
  };

  const handleClear = () => {
    setLocal("");
    onChangeRaw?.("");
    fireNow("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && local.length > 0) {
      e.preventDefault();
      handleClear();
    }
    if (e.key === "Enter") {
      // commit immediately if user wants to skip the debounce
      fireNow(local);
    }
  };

  // Imperative handle for parents that need to clear/focus/flush programmatically.
  useImperativeHandle(
    ref,
    () => ({
      clear: handleClear,
      focus: () => inputRef.current?.focus(),
      flush: () => fireNow(local),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [local, fireNow],
  );

  // Cmd/Ctrl-K global focus shortcut.
  useEffect(() => {
    if (!showShortcutHint) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showShortcutHint]);

  const hasValue = local.length > 0;

  return (
    <div className={`relative ${className}`}>
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none"
        aria-hidden="true"
      />
      <input
        id={id}
        name={name}
        ref={inputRef}
        type="text"
        role="searchbox"
        aria-label={ariaLabel ?? placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder}
        value={local}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className={[
          "w-full pl-10 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-400",
          "focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none",
          "disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed",
          "transition-shadow",
          hasValue ? "pr-9" : showShortcutHint ? "pr-14" : "pr-3",
          sizeMap[size],
          inputClassName,
        ].join(" ")}
      />

      {/* Clear button */}
      {hasValue && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Cmd-K hint */}
      {!hasValue && !disabled && showShortcutHint && (
        <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded">
          ⌘K
        </kbd>
      )}
    </div>
  );
});

export default SearchFilter;
